import { AltitudeUnit } from '../common/enums';

export enum FeServerFlightDetailsRequestFlags {
    None = 0,
    FlownHistory = 1 << 0,
    /**
     * @deprecated Use FlightPlanSummary(4)
     */
    FlightPlanLatest = 1 << 1,
    FlightPlanSummary = 1 << 2,
    FlightPlanHistory = 1 << 3,
    Events = 1 << 4,
    FlightForward = 1 << 5,
    Impacts = 1 << 6
}

export enum FeServerRouteSourceEnum {
    flightPlan,
    atc
}

export enum RouteTypeEnum {
    None = 0,
    OriginalPlan = 1,
    CurrentPlan = 2,
    Latest = 4
}

export enum SpeedUnitEnum {
    Knots = 0,
    Kph = 2,
    Mach = 3
}

export enum WindUnitEnum {
    Knots,
    Kph,
    Mph
}

export enum FeedTypeEnum {
    Type1 = 1,
    Type2 = 2
}

export enum FeServerRequestType {
    FlightData = 1,
    CityPair = 2,
    Event = 3,
    Airport = 4
}

export enum WaypointFunctionType {
    null = 0,
    departureAirport = 1,
    arrivalAirport = 2,
    primaryArrivalAlternateAirport = 3,
    arrivalAlternateAirport = 4,
    enRoute = 5,
    freeFlightEnroute = 6,
    gridPoint = 7,
    ndb = 8,
    vhf = 9,
    compulsory = 10,
    atsItem15 = 11,
    airspaceBoundary = 12,
    oceanicAirspaceBoundary = 13,
    eTOPSEntry = 14,
    eTOPSExit = 15,
    equalTimePoint = 16,
    equalFuelPoint = 17,
    pointOfNoReturn = 18,
    contingencySavingsDecisionWaypoint = 19,
    topOfClimb = 20,
    topOfDescent = 21,
    topOfStepClimb = 22,
    topOfStepDescent = 23,
    bottomOfClimb = 24,
    bottomOfDescent = 25,
    bottomOfStepClimb = 26,
    bottomOfStepDescent = 27,
    ots = 28,
    driftDownDecisionPoint = 29,
    depressurisationDecisionPoint = 30,
    reportingPoint = 31,
    contingencySavingAirport = 32,
    contingencySavingEnRouteAlternateAirport = 33,
    primaryContingencySavingAlternate = 34,
    contingencySavingAlternate = 35,
    eTOPSCoverageEntry = 36,
    eTOPSCoverageExit = 37
}

export interface FeServerRequestBase {
    msgType: FeServerRequestType;
    seqNum: number;
}

export enum FeServerTableType {
    Active,
    New
}

export interface FeServerFlightDataRequest extends FeServerRequestBase {
    msgType: FeServerRequestType.FlightData;
    msgFlags: FeServerFlightDetailsRequestFlags;
    recordNum: number;
    tableType: FeServerTableType;
}

export interface FeServerRouteAiRequest extends FeServerRequestBase {
    msgType: FeServerRequestType.CityPair;
    origin: string;
    dest: string;
}

export interface FeServerEtopsAptModel {
    aft: number; // Airport function type (Adequate, Suitable)
    icaoId: string;
    iataId: string;
}

export interface FeServerEtopsPointModel {
    pt: number; // Point type (ETOPS Entry, ETOPS Exit, ETP, Coverage entry, coverage exit)
    pn: string; // PointName
    lt: number;
    ln: number;
    ct: number | null; // criticalTime
    fob: number | null;
    fu: number | null; // fobUnit (1=lbs, 3=kgs or null)  (volume 2 and 4 not valid)
    bai: number | null; // behind airport index (zero based index into airport list of airport this point is behind in route, or null)
    aai: number | null; // ahead airport index (zero based index into airport list of airport this point is ahead of in route, or null)
}

export interface FeServerEtopsModel {
    bt: number | null; // Border time
    bd: number | null; // Border distance
    rt: number | null; // Rule time
    rd: number | null; // Rule distance
    apt: FeServerEtopsAptModel[];
    pnt: FeServerEtopsPointModel[];
}

export interface FeServerRouteWaypointModel {
    id: string;
    /** Function type */
    tp?: WaypointFunctionType;
    /** Latitude */
    lt: number;
    /** Longitude */
    ln: number;
    /** Planned altitude */
    at?: number;
    /** Planned speed */
    sd?: number;
    /** Planned fuel */
    fob?: number;
    /** Estimated time of ? seconds */
    ete?: number;
    /** Wind */
    wd?: number;
    /** Airway */
    ay?: string;
}

export interface FeServerRouteModel {
    feRouteId: number;
    id?: string;
    rs: FeServerRouteSourceEnum;
    rt: RouteTypeEnum;
    ts: string;
    /** Route text */
    rtxt: string;
    /** Cruising altitude, hundreds of feet */
    ca: number | null;
    /** Waypoints */
    wp: FeServerRouteWaypointModel[];
    /** Altitude units */
    au: AltitudeUnit;
    /** Fuel units */
    fu: FeServerPlannedRouteFuelUnit;
    /** Speed units */
    su: SpeedUnitEnum;
    /** Wind units */
    wu: WindUnitEnum;
    st?: 'S' | 'P' | 'A';
    sn: number | null;
    fd: FeedTypeEnum | null;
    // TODO do we need to type all of this?
    im: [] | null;
    etops: FeServerEtopsModel | null;
    alt: [];
    we: any;
}

export interface FeServerRouteAiSegment {
    sid: string;
    sln: number;
    slt: number;
    eid: string;
    eln: number;
    elt: number;
    pc: number;
    ac: number;
}

export interface RouteAnalyticsModel {
    idest: string;
    iorg: string;
    segments: FeServerRouteAiSegment[];
    tm: string;
}

export interface FeServerResponseBase {
    msgType: FeServerRequestType;
    seqNum: number;
}

export interface FeServerFlightDataResponse {
    msgType: FeServerRequestType.FlightData;
    msgFlags: FeServerFlightDetailsRequestFlags;
    recordNum: number;
    tableType: FeServerTableType;
    routes?: FeServerRouteModel[];
    routeflown?: FeServerRouteFlownItem[];
    eventHist?: FeServerEventHistoryItem[];
    futurePoints?: FeServerFlightForwardItem[];
    //tbfm: [];
    // TODO...
}

export interface FeServerRouteFlownItem {
    lt: number;
    ln: number;
    at?: number;
    sd?: number;
    te?: string | null;
    fd?: number | null;
    fob?: number;
    og?: number;
    fu?: RouteFlownFuelUnit;

    /** Not drawn. This is written by client code to indicate that this position should not be drawn on the map
     * I'll later come down from the server as well
     */
    nd?: boolean;
}

export interface FeServerFlightForwardItem {
    lt: number;
    ln: number;
    at?: number;
    te?: number; // In route flown this is te: string. Try to convince backend guys to provide unix timestamp
}

export enum RouteFlownFuelUnit {
    weightPounds = 0,
    volumeGallons = 1
}

export enum FeServerPlannedRouteFuelUnit {
    Null = 0,
    WeighPounds = 1,
    VolumeGallons = 2,
    WeightKilograms = 3,
    VolumeLiters = 4
}

export enum FlightHistoryItemEnum {
    position,
    event
}

export interface FeServerEventHistoryItem {
    ec: number;
    fd: number | null;
    sr: 0 | 1 | 2;
    st: string | null;
    rt: string | null;
    dt: string | null;
    in: number;
    us: boolean;
}

export interface EventHistoryItem {
    type: FlightHistoryItemEnum.event;
    eventCode: number;
    used: boolean;
    feedId: number | null;
    setReset: 0 | 1 | 2;
    setTime: number | null;
    resetTime: number | null;
    time: number | null;
    details: string | null;
    index: number;
}

export interface FeServerRouteAiResponse {
    msgType: FeServerRequestType.CityPair;
    analytics: RouteAnalyticsModel;
}

export type FeServerResponse = (FeServerFlightDataResponse | FeServerRouteAiResponse) & FeServerResponseBase;
